import { useState } from 'react'

const CARE_STAGES = ['early', 'middle', 'late', 'unknown']

// Comma <-> array helpers for the light-touch list editors.
const toText = (arr) => (arr || []).join(', ')
const toArr = (text) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

export default function ProfileConfirmCard({ profile, saving, onBack, onConfirm }) {
  const [p, setP] = useState({
    full_name: profile.full_name || '',
    preferred_name: profile.preferred_name || '',
    date_of_birth: profile.date_of_birth || '',
    care_stage: profile.care_stage || 'unknown',
    conditions: profile.conditions || [],
    allergies: profile.allergies || [],
    risk_flags: profile.risk_flags || [],
    medications: profile.medications || [],
    initial_tasks: profile.initial_tasks || [],
    care_goals: profile.care_goals || '',
    what_matters_daily: profile.what_matters_daily || '',
    emergency_contact_name: profile.emergency_contact_name || '',
    emergency_contact_phone: profile.emergency_contact_phone || '',
    family_doctor_name: profile.family_doctor_name || '',
    family_doctor_phone: profile.family_doctor_phone || '',
  })

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }))
  const setMed = (i, k, v) =>
    setP((cur) => ({
      ...cur,
      medications: cur.medications.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)),
    }))
  const removeMed = (i) =>
    setP((cur) => ({ ...cur, medications: cur.medications.filter((_, idx) => idx !== i) }))
  const setTask = (i, k, v) =>
    setP((cur) => ({
      ...cur,
      initial_tasks: cur.initial_tasks.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)),
    }))
  const removeTask = (i) =>
    setP((cur) => ({ ...cur, initial_tasks: cur.initial_tasks.filter((_, idx) => idx !== i) }))

  return (
    <div className="screen">
      <div className="container page" style={{ paddingTop: 20 }}>
        <div className="stack">
          <div>
            <span className="badge badge-green">Ginie built this</span>
            <h1 className="title mt-8">Does this look right?</h1>
            <p className="subtitle mt-4">Review and edit anything before we save it.</p>
          </div>

          <div className="card stack">
            <div className="field">
              <label className="label">Full name</label>
              <input className="input" value={p.full_name} onChange={(e) => set('full_name', e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field grow">
                <label className="label">Preferred name</label>
                <input className="input" value={p.preferred_name} onChange={(e) => set('preferred_name', e.target.value)} />
              </div>
              <div className="field grow">
                <label className="label">Date of birth</label>
                <input type="date" className="input" value={p.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label className="label">Care stage</label>
              <select className="select" value={p.care_stage} onChange={(e) => set('care_stage', e.target.value)}>
                {CARE_STAGES.map((s) => (
                  <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card stack">
            <div className="field">
              <label className="label">Conditions</label>
              <input className="input" value={toText(p.conditions)} onChange={(e) => set('conditions', toArr(e.target.value))} placeholder="Comma separated" />
            </div>
            <div className="field">
              <label className="label">Allergies</label>
              <input className="input" value={toText(p.allergies)} onChange={(e) => set('allergies', toArr(e.target.value))} placeholder="Comma separated" />
            </div>
            <div className="field">
              <label className="label">Risk flags</label>
              <input className="input" value={toText(p.risk_flags)} onChange={(e) => set('risk_flags', toArr(e.target.value))} placeholder="e.g. Fall risk, Wandering" />
            </div>
          </div>

          {/* Medications */}
          <div className="card stack">
            <div className="spread">
              <div className="section-title">Medications</div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => set('medications', [...p.medications, { name: '', dose: '', timing: '' }])}
              >
                + Add
              </button>
            </div>
            {p.medications.length === 0 && <p className="muted" style={{ fontSize: 14, margin: 0 }}>None yet.</p>}
            {p.medications.map((m, i) => (
              <div key={i} className="stack" style={{ gap: 6, paddingBottom: 10, borderBottom: i < p.medications.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div className="row" style={{ gap: 8 }}>
                  <input className="input grow" value={m.name || ''} onChange={(e) => setMed(i, 'name', e.target.value)} placeholder="Name" />
                  <button onClick={() => removeMed(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 14 }}>Remove</button>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <input className="input grow" value={m.dose || ''} onChange={(e) => setMed(i, 'dose', e.target.value)} placeholder="Dose" />
                  <input className="input grow" value={m.timing || ''} onChange={(e) => setMed(i, 'timing', e.target.value)} placeholder="Timing" />
                </div>
              </div>
            ))}
          </div>

          <div className="card stack">
            <div className="field">
              <label className="label">Care goals</label>
              <textarea className="textarea" value={p.care_goals} onChange={(e) => set('care_goals', e.target.value)} />
            </div>
            <div className="field">
              <label className="label">What matters day to day</label>
              <textarea className="textarea" value={p.what_matters_daily} onChange={(e) => set('what_matters_daily', e.target.value)} />
            </div>
          </div>

          <div className="card stack">
            <div className="row" style={{ gap: 10 }}>
              <div className="field grow">
                <label className="label">Emergency contact</label>
                <input className="input" value={p.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} placeholder="Name" />
              </div>
              <div className="field grow">
                <label className="label">&nbsp;</label>
                <input className="input" value={p.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} placeholder="Phone" />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field grow">
                <label className="label">Family doctor</label>
                <input className="input" value={p.family_doctor_name} onChange={(e) => set('family_doctor_name', e.target.value)} placeholder="Name" />
              </div>
              <div className="field grow">
                <label className="label">&nbsp;</label>
                <input className="input" value={p.family_doctor_phone} onChange={(e) => set('family_doctor_phone', e.target.value)} placeholder="Phone" />
              </div>
            </div>
          </div>

          {/* Tasks */}
          <div className="card stack">
            <div className="spread">
              <div className="section-title">First tasks</div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => set('initial_tasks', [...p.initial_tasks, { title: '', description: '' }])}
              >
                + Add
              </button>
            </div>
            {p.initial_tasks.length === 0 && <p className="muted" style={{ fontSize: 14, margin: 0 }}>None yet.</p>}
            {p.initial_tasks.map((t, i) => (
              <div key={i} className="stack" style={{ gap: 6 }}>
                <div className="row" style={{ gap: 8 }}>
                  <input className="input grow" value={t.title || ''} onChange={(e) => setTask(i, 'title', e.target.value)} placeholder="Task" />
                  <button onClick={() => removeTask(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 14 }}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <p className="muted" style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            All set! You can add medications, reports, and appointments anytime.
            Ginie is always here if you need to update anything.
          </p>

          <div className="row" style={{ gap: 10, paddingBottom: 20 }}>
            <button className="btn btn-ghost" onClick={onBack} disabled={saving}>Keep talking</button>
            <button className="btn btn-lg grow" onClick={() => onConfirm(p)} disabled={saving || !p.full_name.trim()}>
              {saving ? <span className="spinner" /> : 'Save & continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
